import { Icons } from "assets";
import classNames from "classnames";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import CustomSelect from "shared/components/common/customSelect";
import useDebounce from "shared/hooks/useDebounce";
import { CheckJobCodeService } from "shared/services/evaluations";
import { JobRole } from "shared/utils/enum";
import { hasChanges } from "shared/utils/helper";
import { QuestionnaireStep1VS } from "shared/utils/validation";
import styles from "./style.module.scss";

interface formikValuesInterface {
  jobs: QuestionnaireStep1Type[];
}

interface QuestionnaireStep1Props {
  formikRef: any;
  formData: any;
  isEdit?: boolean;
  evalsLoading?: boolean;
  jobCodeError: any[];
  setAddEvent: (val: boolean) => void;
  setJobCodeError: (val: any[]) => void;
}

const QuestionnaireStep1 = ({
  formikRef,
  formData,
  isEdit,
  evalsLoading,
  setAddEvent,
  setJobCodeError,
  jobCodeError,
}: QuestionnaireStep1Props) => {
  const {
    theme: { theme },
  } = useSelector((state: any) => state?.root);

  const initialValues: formikValuesInterface = {
    jobs: [
      {
        jobTitle: "",
        department: "",
        jobCode: "",
        jobGrade: "",
        roleType: null,
        rasScore: 0,
        rasLevel: 0,
        shortCode: "",
        id: "0",
      },
    ],
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: QuestionnaireStep1VS,
    onSubmit: (value, action) => {
      action.setSubmitting(false);
    },
  });
  const { handleChange, values, touched, errors, setFieldError } = formik;

  const handleAddJobForm = () => {
    formik.setFieldValue("jobs", [
      ...values.jobs,
      {
        jobTitle: "",
        department: "",
        jobCode: "",
        jobGrade: "",
        roleType: null,
        rasScore: 0,
        rasLevel: 0,
        shortCode: "",
        id:
          values.jobs?.length > 0
            ? String(
                Number(Number(values.jobs?.[values.jobs.length - 1]?.id) + 1)
              )
            : "0",
      },
    ]);
    setJobCodeError([
      ...jobCodeError,
      {
        message: "",
        error: false,
      },
    ]);
  };

  const handleRemoveJobForm = (index: number) => {
    const newJobs = [...values.jobs];
    newJobs.splice(index, 1);
    formik.setFieldValue("jobs", newJobs);
    const newErrors = [...jobCodeError];
    newErrors.splice(index, 1);
    setJobCodeError(newErrors);
  };

  const handleInitialSetup = () => {
    let jobs = formData;
    let fields: any = [];
    for (let k = 0; k < jobs?.length; k++) {
      let obj = {
        id: jobs?.[k]?.id ? jobs?.[k]?.id : "0",
        jobTitle: jobs?.[k]?.jobTitle,
        department: jobs?.[k]?.department,
        jobCode: jobs?.[k]?.jobCode,
        jobGrade: jobs?.[k]?.jobGrade,
        roleType: jobs?.[k]?.roleType,
        rasScore: jobs?.[k]?.rasScore ? jobs?.[k]?.rasScore : 0,
        rasLevel: jobs?.[k]?.rasLevel ? jobs?.[k]?.rasLevel : 0,
        shortCode: jobs?.[k]?.shortCode ? jobs?.[k]?.shortCode : "",
      };
      fields.push(obj);
    }
    formik.setFieldValue("jobs", fields);
  };

  useEffect(() => {
    formikRef.current = formik;
    // eslint-disable-next-line
  }, [formik]);

  useEffect(() => {
    if (formData?.length > 0 && !evalsLoading) {
      handleInitialSetup();
    }
    // eslint-disable-next-line
  }, [evalsLoading, formData]);

  useEffect(() => {
    if (hasChanges(values.jobs, initialValues.jobs?.[0])) {
      setAddEvent?.(true);
    }
    // eslint-disable-next-line
  }, [values.jobs]);

  return (
    <div className={classNames(styles.stepOneContainer)}>
      <div className={classNames(styles.head)}>
        <h1>List the role(s) you would like to evaluate:</h1>
      </div>

      <form
        className={classNames(styles.tableWrapper)}
        onSubmit={(e) => e.preventDefault()}
      >
        <table className="table" style={{ minWidth: "800px", margin: "0px" }}>
          <thead>
            <tr style={{ height: "44px" }}>
              <th className={classNames(styles.tableHead)}>
                Job Title <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>
                Department <span>*</span>
              </th>
              <th className={classNames(styles.tableHead)}>Job Code</th>
              <th className={classNames(styles.tableHead)}>Job Grade</th>
              <th className={classNames(styles.tableHead)}>
                Role Type <span>*</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {values.jobs.map((job, index) => {
              return (
                <RowItem
                  job={job}
                  index={index}
                  key={index}
                  formik={formik}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  handleRemoveJobForm={handleRemoveJobForm}
                  setFieldError={setFieldError}
                  isEdit={isEdit}
                  jobCodeError={jobCodeError}
                  setJobCodeError={setJobCodeError}
                />
              );
            })}
          </tbody>
        </table>
      </form>
      {!isEdit ? (
        <CustomButton
          title="Add More Roles"
          Icon={Icons.Plus}
          containerStyle={classNames(
            theme === "dark" ? styles.btnStyle : styles.lighThemeBtnStyle,
            "gap-1 mt-3"
          )}
          IconDirction="left"
          onClick={() => {
            handleAddJobForm();
          }}
        />
      ) : null}
    </div>
  );
};

const RowItem = ({
  job,
  touched,
  index,
  handleChange,
  formik,
  errors,
  handleRemoveJobForm,
  setFieldError,
  isEdit,
  jobCodeError,
  setJobCodeError,
}: any) => {
  const options = [
    {
      title: "Management",
      Icon: null,
      action: (index: number) => {
        formik.setFieldValue(`jobs[${index}].roleType`, {
          name: "Management",
          value: JobRole.management,
        });
      },
    },
    {
      title: "Individual Contributor",
      Icon: null,
      action: (index: number) => {
        formik.setFieldValue(`jobs[${index}].roleType`, {
          name: "Individual Contributor",
          value: JobRole.individualContributor,
        });
      },
    },
  ];

  const handleCheckJobCode = () => {
    if (!isEdit) {
      CheckJobCodeService({ jobCode: job.jobCode ?? "" })
        .then(({ data }) => {
          if (data === 1) {
            setFieldError(
              `jobs.[${index}].jobCode`,
              "This Job Code Already exists"
            );
            let temp = [...jobCodeError];
            temp[index] = {
              message: "This Job Code Already exists",
              error: true,
            };
            setJobCodeError(temp);
          } else {
            let temp = [...jobCodeError];
            temp[index] = {
              message: "",
              error: false,
            };
            setJobCodeError(temp);
          }
        })
        .catch((err) => {
          let temp = [...jobCodeError];
          temp[index] = {
            message: "",
            error: false,
          };
          setJobCodeError(temp);
        });
    }
  };

  useDebounce(
    () => {
      handleCheckJobCode();
    },
    [job.jobCode],
    800
  );

  return (
    <tr key={index} className="relative">
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          required
          placeholder="Enter Job Title"
          value={job.jobTitle}
          onChange={handleChange("jobs." + index + ".jobTitle")}
          error={
            touched.jobs?.[index]?.jobTitle &&
            //@ts-ignore
            errors.jobs?.[index]?.jobTitle
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          required
          placeholder="Enter Department"
          value={job.department}
          onChange={handleChange("jobs." + index + ".department")}
          error={
            touched.jobs?.[index]?.department &&
            //@ts-ignore
            errors.jobs?.[index]?.department
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          placeholder="Enter Job Code"
          value={job.jobCode}
          onChange={handleChange("jobs." + index + ".jobCode")}
          error={
            //@ts-ignore
            errors.jobs?.[index]?.jobCode || jobCodeError[index]?.message
          }
          notRequiredMargin
          readOnly={isEdit}
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <CustomInput
          placeholder="Enter Job Grade"
          value={job.jobGrade}
          onChange={handleChange("jobs." + index + ".jobGrade")}
          error={
            touched.jobs?.[index]?.jobGrade &&
            //@ts-ignore
            errors.jobs?.[index]?.jobGrade
          }
          notRequiredMargin
          customInputContainer={classNames(styles.customInputStyle)}
        />
      </td>
      <td
        className={classNames(styles.tableContentLabel)}
        style={{ width: "20%" }}
      >
        <div className={classNames("flex items-center gap-2")}>
          <CustomSelect
            placeholder="Select Role Type"
            required
            options={options}
            value={job.roleType?.name}
            error={
              touched.jobs?.[index]?.roleType &&
              //@ts-ignore
              errors.jobs?.[index]?.roleType
            }
            tabIndex={index}
            notRequiredMargin
            customInputContainer={classNames(styles.selectionContainer)}
          />
          {!isEdit && formik?.values.jobs?.length !== 1 ? (
            <div
              className={classNames(styles.trashIconContainer)}
              onClick={() => {
                if (formik?.values.jobs?.length > 1) {
                  handleRemoveJobForm(index);
                }
              }}
            >
              <Icons.Cross />
            </div>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

export default QuestionnaireStep1;
